import { Injectable, Signal, computed, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from 'environments/environment';

import { DEFAULT_VENUE_MODULE, VenueModule } from './module-version-menu';
import { WithoutInterceptor } from './without.interceptor';

/** sessionStorage cache key — lives alongside CURRENT_USER / MODULE_VERSIONS. */
export const VENUE_MODULE_STORAGE_KEY = 'VENUE_MODULE';

/**
 * Per-temple venue module switch (WS-FHP owner decision D-FHP-9:
 * `VENUE_MODULE` = AUDITORIUM | FUNCTION_HALL, strictly either/or, default
 * AUDITORIUM).
 *
 * Backend contract (FROZEN, otas_backend/docs/FUNCTION_HALL_PRIEST_MODULE.md
 * §6.4/§6.5):
 *   GET v2/function-hall/config → { result: { venueModule: 'AUDITORIUM', ... } }
 *   PUT v2/function-hall/config  (the Function Hall Settings screen owns writes)
 *
 * Deliberately modelled on `ModuleVersionService` (the mechanism §6.8 says to
 * reuse): a signal seeded from the sessionStorage cache so a reload inside a
 * session renders the correct menu before the refetch lands, `ensureLoaded()`
 * fetched once per boot from the nav menu, and a pure
 * `applyVenueModuleToMenus` transform in the MODULE_ROUTE_MAP neighbourhood.
 *
 * READS `GET v2/venue-module` — a menu-safe feed on the same broad guard the
 * module-version feed uses, NOT `v2/function-hall/config`. The config endpoint
 * is guarded `functionHallBillingAccess` and answers 403 for a pooja-only user,
 * which would make every menu build emit an "insufficient permission" toast and
 * silently fall back to the default. Writes still go through the Function Hall
 * Settings screen (`PUT v2/function-hall/config`), which stays FULL-guarded.
 *
 * It still fetches through `WithoutInterceptor` so that a transport failure
 * (offline, an older backend without the endpoint) leaves the cached/default
 * value in force silently instead of toasting on the login path.
 */
@Injectable({ providedIn: 'root' })
export class VenueModuleService {

    private readonly venue = signal<VenueModule>(this.readCache());
    private loaded = false;
    private inflight$: Observable<VenueModule> | null = null;

    /** Reactive venue module — AUDITORIUM before/without any data. */
    readonly venueModule: Signal<VenueModule> = computed(() => this.venue());

    constructor(private http: WithoutInterceptor) { }

    /** Current value (non-reactive readers: MenuService.setMenuITEMS). */
    current(): VenueModule {
        return this.venue();
    }

    /**
     * Fetches the venue module once per app boot (subsequent calls replay).
     * Never errors — on failure (offline, 403 for a user without the function
     * hall roles) the cached/default value stays in force and a later call
     * retries.
     */
    ensureLoaded(): Observable<VenueModule> {
        if (this.loaded) {
            return of(this.venue());
        }
        if (!this.inflight$) {
            this.inflight$ = this.http.get<any>(environment.apiUrl + 'v2/venue-module').pipe(
                map(resp => this.coerce(resp?.result?.venueModule)),
                tap(next => {
                    this.loaded = true;
                    this.venue.set(next);
                    this.writeCache(next);
                }),
                catchError(() => {
                    this.inflight$ = null; // allow a later retry
                    return of(this.venue());
                }),
                shareReplay(1)
            );
        }
        return this.inflight$;
    }

    /** Forces a refetch (after login — the same window may re-login). */
    refresh(): Observable<VenueModule> {
        this.loaded = false;
        this.inflight$ = null;
        return this.ensureLoaded();
    }

    /**
     * Adopts a value the Function Hall Settings screen has just saved (that
     * screen owns `PUT v2/function-hall/config`, which carries the whole
     * config object). Updating the signal re-wires this window's menu live.
     */
    applyLocal(value: unknown): void {
        const next = this.coerce(value);
        this.loaded = true;
        this.venue.set(next);
        this.writeCache(next);
    }

    private coerce(value: unknown): VenueModule {
        return value === 'FUNCTION_HALL' ? 'FUNCTION_HALL' : DEFAULT_VENUE_MODULE;
    }

    private readCache(): VenueModule {
        try {
            return this.coerce(sessionStorage.getItem(VENUE_MODULE_STORAGE_KEY));
        } catch {
            return DEFAULT_VENUE_MODULE;
        }
    }

    private writeCache(value: VenueModule): void {
        try {
            sessionStorage.setItem(VENUE_MODULE_STORAGE_KEY, value);
        } catch {
            // storage unavailable — the signal still holds it for this session
        }
    }
}
